import { useCallback, useState } from 'react';
import { Dashboard } from 'api/dashboard';
import { DashboardForm } from '../dashboard-constants';

interface ModalInfo {
  initialValue?: DashboardForm;
  extraProps?: {
    dashboardId: number;
    isDefault: boolean;
  };
  variant: DashboardModelVariant;
}

type UseDashboardManageModal = {
  variant: DashboardModelVariant;
  modalProps: {
    dashboardId: number;
    isDefault?: boolean;
    initialValue?: DashboardForm;
  };
  modalActions: ModelAction;
};

export enum DashboardModelVariant {
  Edit = 'edit',
  Create = 'create',
  Duplicate = 'duplicate',
  None = 'none',
}
const initialState = {
  initialValue: undefined,
  modalProps: undefined,
  variant: DashboardModelVariant.None,
};

export interface ModelAction {
  setModalClose: () => void;
  setModalOpen: (variant: DashboardModelVariant, init?: Dashboard) => void;
}

export const useDashboardManageModal = (): UseDashboardManageModal => {
  const [modelInfo, setModalInfo] = useState<ModalInfo>(initialState);

  const setModalClose = useCallback(() => {
    setModalInfo(initialState);
  }, []);

  const setModalOpenForCreate = () => {
    setModalInfo({
      variant: DashboardModelVariant.Create,
      initialValue: undefined,
      extraProps: undefined,
    });
  };

  const setModalOpenWithDashboard = (
    variant: DashboardModelVariant,
    dashboard: Dashboard,
  ) =>
    setModalInfo({
      variant,
      initialValue: {
        dashboardName: dashboard.dashboardName,
        shared: dashboard.shared,
        dashboardId: dashboard.dashboardId,
      },
      extraProps: {
        dashboardId: dashboard.dashboardId,
        isDefault: dashboard.isDefault,
      },
    });

  const setModalOpen = (
    variant: DashboardModelVariant,
    dashboard?: Dashboard,
  ) => {
    if (variant === DashboardModelVariant.Create) {
      setModalOpenForCreate();

      return;
    }
    if (!dashboard) {
      return;
    }
    setModalOpenWithDashboard(variant, dashboard);
  };

  const { variant, initialValue, extraProps } = modelInfo;
  const modalProps = {
    initialValue,
    dashboardId: extraProps?.dashboardId ?? -1,
    isDefault: extraProps?.isDefault,
  };

  return {
    variant,
    modalProps,
    modalActions: { setModalClose, setModalOpen },
  };
};
